import React, { Fragment, useEffect, useState } from 'react'
import './dashboard-v2.scss'
import { DashboardV2Presenter } from './dashboard-v2-presenter'
import { WithdrawalRequestFormModal } from './components/WithdrawalRequestForm'
import { CancelWithdrawalModal } from './components/CancelWithdrawalModal'
import { OngoingWithdrawalDetailsModal } from './components/OngoingWithdrawalDetailsModal'
import { OngoingInvestmentDetailsModal } from './components/OngoingInvestmentDetailsModal'
import { TransactionDetailsModal } from './components/TransactionDetailsModal'
import { CancelInvestmentModal } from '../dashboard-new/cancel-investment-modal'
import type { OngoingInvestmentRow } from './components/OngoingInvestmentsSection'
import { useDispatch, useSelector } from 'react-redux'
import { useNavigate } from 'react-router-dom'
import { RootState } from '../../store'
import {
  dashboardServices,
  investServices,
  kycServices,
  portfolioStatementsServices,
  PortfolioStatement,
  secondaryMarketServices,
  userServices,
} from '../../services'
import {
  DashboardHoldings,
  DashboardInvestmentDistribution,
  DashboardInvestmentGraph,
  DashboardInvestmentGraphValues,
  DashboardInvestmentsGraphs,
  DashboardInvestmentsSingleGraph,
  DashboardSummary,
  Investment,
  InvestmentStatus_v1,
  RawOpportunity,
  UserDetails,
  UserInvestmentDetails,
  DashboardInvestmentTransactions,
  Withdrawal,
} from '../../lib/types/propsType_v1'
import { calculateStartDateFromFilter, createSlug } from '../../lib/utils/helpers'
import { showBanner } from '../../global-state/banner-slice'
import { dashboardGraphFilter } from '../../lib/utils/message'
import { DashboardV2InvestmentView } from './dashboard-v2.types'
import {
  buildDistributionItems,
  buildPerformanceSeries,
  buildStatusBreakdownData,
  centsToCurrency,
  getLastUpdatedText,
} from './dashboard-v2.utils'
import ExtendLimitModal from '../../lib/components/ExtendLimitModal/extend-limit-modal'
import { usePendingInvestmentLimitRequest } from '../../lib/utils/use-pending-investment-limit-request'
import {
  buildHoldingsSnapshotData,
  buildPerfSeriesDollars,
  getInvestmentLimitSummary,
  getInvestorIdentity,
  mapHoldingTransactionHistory,
  fetchAllWithdrawalsForInvestor,
} from './dashboard-v2-container.helpers'

const ONGOING_INVESTMENT_STATUSES = [
  InvestmentStatus_v1.INITIATED,
  InvestmentStatus_v1.PAYMENT_DONE,
  InvestmentStatus_v1.ORDER_PLACED,
] as const

const DashboardV2Container = () => {
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { authToken, email, name, kycStatus, userId, investorReference } = useSelector(
    (state: RootState) => state.investor,
  )

  const [dashboardSummary, setDashboardSummary] = useState<DashboardSummary>({
    totalInvestedAmount: 0,
    totalReturnsTillDate: 0,
    realizedGains: 0,
    unrealizedGains: 0,
    realizedGainsAmount: 0,
    unrealizedGainsAmount: 0,
    totalUnits: 0,
  })
  const [investmentDistribution, setInvestmentDistribution] =
    useState<DashboardInvestmentDistribution>({})
  const [investmentsGraphData, setInvestmentsGraphData] = useState<DashboardInvestmentsGraphs>({
    investments: [[]],
  })
  const [holdingsData, setHoldingsData] = useState<DashboardHoldings[]>([])
  const [investments, setInvestments] = useState<Array<Investment & RawOpportunity & UserDetails>>(
    [],
  )
  const [transactionsData, setTransactionsData] = useState<DashboardInvestmentTransactions[]>([])
  const [userInvestmentDetail, setUserInvestmentDetail] = useState<UserInvestmentDetails>()
  const [kycStatusData, setKycStatusData] = useState(kycStatus)
  const [clientId, setClientId] = useState<string | undefined>(investorReference || undefined)
  const [selectedRange, setSelectedRange] = useState<string>(dashboardGraphFilter[4].value)
  const [investmentView, setInvestmentView] = useState<DashboardV2InvestmentView>('snapshot')
  const [isStatementsMenuOpen, setIsStatementsMenuOpen] = useState(false)
  const [portfolioStatements, setPortfolioStatements] = useState<PortfolioStatement[]>([])
  const [portfolioStatementsLoading, setPortfolioStatementsLoading] = useState(false)
  const [downloadingStatementId, setDownloadingStatementId] = useState<string | null>(null)
  const [redeemHolding, setRedeemHolding] = useState<DashboardHoldings | null>(null)
  const [withdrawalsData, setWithdrawalsData] = useState<Withdrawal[]>([])
  const [investmentToCancel, setInvestmentToCancel] = useState<OngoingInvestmentRow | null>(null)
  const [investmentToView, setInvestmentToView] = useState<OngoingInvestmentRow | null>(null)
  const [withdrawalToCancel, setWithdrawalToCancel] = useState<Withdrawal | null>(null)
  const [withdrawalToView, setWithdrawalToView] = useState<Withdrawal | null>(null)
  const [transactionToView, setTransactionToView] =
    useState<DashboardInvestmentTransactions | null>(null)
  const [showExtendLimitModal, setShowExtendLimitModal] = useState(false)
  const [showSecondaryMarketLink, setShowSecondaryMarketLink] = useState(false)
  const { hasPendingRequest, markPending, openIfAllowed } = usePendingInvestmentLimitRequest()

  const reloadOngoingInvestments = async () => {
    if (!(authToken && userId)) return
    const statusQuery = ONGOING_INVESTMENT_STATUSES.map((s) => `status=${s}`).join('&')
    const invResp = await investServices.searchInvestments_v1(
      { limit: 20, offset: 0, sortBy: 'NEWEST', investor_id: userId },
      { Authorization: authToken },
      `?${statusQuery}`,
    )
    if (invResp?.status === 200) setInvestments(invResp.investments)
  }

  const reloadWithdrawals = async () => {
    if (!(authToken && userId)) return
    try {
      const wdList = await fetchAllWithdrawalsForInvestor(userId, authToken)
      setWithdrawalsData(wdList)
    } catch {
      setWithdrawalsData([])
    }
  }

  useEffect(() => {
    if (!(authToken && userId)) return

    let isMounted = true
    const load = async () => {
      const authHeader = { Authorization: authToken || '' }

      const [summaryRes, holdingsRes, investmentsRes, userInvestmentRes, kycRes, profileRes] =
        await Promise.allSettled([
          dashboardServices.getDashboardSummary(authHeader),
          dashboardServices.getInvestorHoldings(
            { limit: '10', offset: '0', sortBy: 'NEWEST' },
            authHeader,
          ),
          investServices.searchInvestments_v1(
            { limit: 20, offset: 0, sortBy: 'NEWEST', investor_id: userId },
            authHeader,
            `?${ONGOING_INVESTMENT_STATUSES.map((s) => `status=${s}`).join('&')}`,
          ),
          investServices.getUserInvestmentDetail_v1(authHeader),
          kycServices.getKYC_v1(userId.toString(), authHeader),
          userServices.getProfileForCurrentUser_v1(authHeader),
        ])

      if (!isMounted) return

      if (summaryRes.status === 'fulfilled') {
        const summaryResp = summaryRes.value
        if (summaryResp?.status === 200 && summaryResp?.data) {
          const { investmentDistributions, ...summary } = summaryResp.data
          const distributionData =
            investmentDistributions?.reduce(
              (acc: { [key in string]: number }, curr: { ratio: number; asset_class: string }) => {
                acc[curr.asset_class] = curr.ratio
                return acc
              },
              {},
            ) ?? {}
          setInvestmentDistribution(distributionData)
          setDashboardSummary(summary)
        }
      }

      if (holdingsRes.status === 'fulfilled') {
        const holdingsResp = holdingsRes.value
        if (holdingsResp?.status === 200) {
          const holdings = holdingsResp?.holdings ?? []
          setHoldingsData(holdings)
          const transactionHistory = Array.isArray(holdingsResp?.transactionHistory)
            ? holdingsResp.transactionHistory
            : []
          setTransactionsData(mapHoldingTransactionHistory(holdings, transactionHistory))
        }
      }

      if (investmentsRes.status === 'fulfilled') {
        const invResp = investmentsRes.value
        if (invResp?.status === 200) setInvestments(invResp?.investments)
      }

      if (userInvestmentRes.status === 'fulfilled') {
        const userInvDetailResp = userInvestmentRes.value
        if (userInvDetailResp?.status === 200 && userInvDetailResp?.data) {
          setUserInvestmentDetail(userInvDetailResp.data)
        }
      }

      if (kycRes.status === 'fulfilled') {
        const kycResp = kycRes.value
        if (kycResp?.data) {
          setKycStatusData(kycResp.data.status ?? '')
        }
      }

      if (profileRes.status === 'fulfilled') {
        const profileResp = profileRes.value
        const investorRef = profileResp?.data?.investor_reference
        if (investorRef) setClientId(investorRef)
      }

      try {
        const wdList = await fetchAllWithdrawalsForInvestor(userId, authToken || '')
        if (isMounted) setWithdrawalsData(wdList)
      } catch {
        if (isMounted) setWithdrawalsData([])
      }
    }

    load().catch((error) => {
      console.error('Failed to load dashboard-v2 data', error)
    })
    return () => {
      isMounted = false
    }
  }, [authToken, userId])

  useEffect(() => {
    if (!(authToken && userId)) return

    const loadGraph = async () => {
      const startDate = calculateStartDateFromFilter(selectedRange)
      const activeResp = await dashboardServices.getActiveInvestments(
        { startDate },
        { Authorization: authToken || '' },
      )
      if (activeResp?.status === 200 && activeResp?.data) {
        const inv = activeResp.data?.investments?.reduce(
          (acc: Array<DashboardInvestmentsSingleGraph[]>, curr: DashboardInvestmentGraph) => {
            const current: DashboardInvestmentsSingleGraph[] =
              curr?.values?.map((item: DashboardInvestmentGraphValues) => ({
                date: item.date,
                currentInvestmentValue: item.currentInvestmentValue,
                opportunity_id: curr.opportunity_id,
                opportunity_title: curr.opportunity_title,
              })) ?? []
            return [...acc, current]
          },
          [],
        )
        setInvestmentsGraphData({ investments: inv })
      }
    }

    loadGraph()
  }, [authToken, userId, selectedRange])

  useEffect(() => {
    if (!authToken) return
    const loadSecondaryMarketVisibility = async () => {
      const portal = await secondaryMarketServices.getPortal()
      if (!portal) {
        setShowSecondaryMarketLink(false)
        return
      }
      const hasOrders = (portal.myOrders?.length ?? 0) > 0
      setShowSecondaryMarketLink(Boolean(portal.settings?.is_open) || hasOrders)
    }
    loadSecondaryMarketVisibility()
  }, [authToken])

  useEffect(() => {
    if (!authToken) return

    const loadPortfolioStatements = async () => {
      setPortfolioStatementsLoading(true)
      try {
        const response = await portfolioStatementsServices.listMyStatements({
          Authorization: authToken || '',
        })
        if (response?.status === 200 && Array.isArray(response?.data?.statements)) {
          setPortfolioStatements(response.data.statements)
        } else {
          setPortfolioStatements([])
          dispatch(
            showBanner({
              text: 'Failed to load portfolio statements.',
              variant: 'error-banner',
            }),
          )
        }
      } catch {
        setPortfolioStatements([])
        dispatch(
          showBanner({
            text: 'Failed to load portfolio statements.',
            variant: 'error-banner',
          }),
        )
      } finally {
        setPortfolioStatementsLoading(false)
      }
    }

    loadPortfolioStatements()
  }, [authToken, dispatch])

  const { investorDisplayName, investorInitial } = getInvestorIdentity(name, email)
  const { totalInvestment, investmentLimit, usedPct, remaining, activeHoldings } =
    getInvestmentLimitSummary(userInvestmentDetail, holdingsData)
  const perfSeriesCents = buildPerformanceSeries(investmentsGraphData)
  const perfSeriesDollars = buildPerfSeriesDollars(perfSeriesCents)
  const currentValueText = centsToCurrency(perfSeriesCents?.[perfSeriesCents.length - 1]?.value)
  const lastUpdatedText = getLastUpdatedText()
  const distributionItems = buildDistributionItems(investmentDistribution)
  const statusBreakdownData = buildStatusBreakdownData(investments)
  const holdingsSnapshotData = buildHoldingsSnapshotData(holdingsData)

  const downloadPortfolioStatement = async (statementId: string) => {
    if (!authToken || downloadingStatementId) return

    setDownloadingStatementId(statementId)
    try {
      const response = await portfolioStatementsServices.downloadStatement(statementId, {
        Authorization: authToken || '',
      })
      const url = response?.data?.url
      if (response?.status === 200 && url) {
        window.open(url, '_blank', 'noopener,noreferrer')
        return
      }

      const notFound = response?.status === 404
      dispatch(
        showBanner({
          text: notFound
            ? 'Portfolio statement not found.'
            : 'Failed to download portfolio statement.',
          variant: 'error-banner',
        }),
      )
    } catch {
      dispatch(
        showBanner({
          text: 'Failed to download portfolio statement.',
          variant: 'error-banner',
        }),
      )
    } finally {
      setDownloadingStatementId(null)
    }
  }

  const resolvedClientId = clientId || investorReference || undefined

  return (
    <Fragment>
      <DashboardV2Presenter
        investorDisplayName={investorDisplayName}
        investorInitial={investorInitial}
        kycStatus={kycStatusData}
        clientId={resolvedClientId}
        dashboardSummary={dashboardSummary}
        isStatementsMenuOpen={isStatementsMenuOpen}
        portfolioStatements={portfolioStatements}
        portfolioStatementsLoading={portfolioStatementsLoading}
        downloadingStatementId={downloadingStatementId}
        investmentView={investmentView}
        holdingsData={holdingsData}
        totalInvestment={totalInvestment}
        investmentLimit={investmentLimit}
        usedPct={usedPct}
        remaining={remaining}
        activeHoldings={activeHoldings}
        perfSeriesDollars={perfSeriesDollars}
        currentValueText={currentValueText}
        lastUpdatedText={lastUpdatedText}
        distributionItems={distributionItems}
        statusBreakdownData={statusBreakdownData}
        holdingsSnapshotData={holdingsSnapshotData}
        transactionsData={transactionsData}
        onTransactionClick={(txn) => setTransactionToView(txn)}
        ongoingInvestmentsData={investments}
        onOngoingInvestmentContinue={(inv) => {
          const titleSegment = inv.opportunity_title
            ? createSlug(inv.opportunity_title).replace(/^-+|-+$/g, '') || 'opportunity'
            : 'opportunity'
          navigate(`/invest-v2/${titleSegment}/${inv.opportunity_id}`)
        }}
        onOngoingInvestmentViewDetails={(inv) => setInvestmentToView(inv)}
        onOngoingInvestmentCancel={(inv) => setInvestmentToCancel(inv)}
        withdrawalsData={withdrawalsData}
        onWithdrawalViewDetails={(wd) => setWithdrawalToView(wd)}
        onWithdrawalCancel={(wd) => setWithdrawalToCancel(wd)}
        selectedRange={selectedRange}
        onRangeChange={setSelectedRange}
        onToggleStatements={() => setIsStatementsMenuOpen((prev) => !prev)}
        onDownloadStatement={downloadPortfolioStatement}
        onNewInvestment={() => navigate('/opportunities')}
        showSecondaryMarketLink={showSecondaryMarketLink}
        onSecondaryMarket={() => navigate('/dashboard/secondary-market')}
        onInvestmentViewChange={setInvestmentView}
        onHoldingClick={(opportunityId) => navigate(`/dashboard/investments/${opportunityId}`)}
        onHoldingMenuAction={(opportunityId, action) => {
          if (action === 'switch_fund') {
            navigate('/opportunities')
            return
          }
          if (action === 'redeem') {
            const row = holdingsData.find((h) => h.opportunity_id === opportunityId)
            if (row) setRedeemHolding(row)
            return
          }
          if (action === 'investment_details') {
            const row = holdingsData.find((h) => h.opportunity_id === opportunityId)
            const titleSegment = row?.opportunity_title
              ? createSlug(row.opportunity_title).replace(/^-+|-+$/g, '') || 'opportunity'
              : ''
            if (titleSegment) {
              navigate(`/opportunity/${titleSegment}/${opportunityId}`)
              return
            }
            navigate(`/dashboard/investments/${opportunityId}`)
            return
          }
          navigate(`/dashboard/investments/${opportunityId}`)
        }}
        onRequestLimitExtension={() => void openIfAllowed(() => setShowExtendLimitModal(true))}
        hasPendingLimitRequest={hasPendingRequest}
        onWallet={() => navigate('/dashboard/wallet')}
      />
      {showExtendLimitModal ? (
        <ExtendLimitModal
          onClose={() => setShowExtendLimitModal(false)}
          onSubmitted={markPending}
        />
      ) : null}
      <WithdrawalRequestFormModal
        isOpen={redeemHolding !== null}
        holding={redeemHolding}
        investorDisplayName={investorDisplayName}
        clientId={resolvedClientId}
        userId={userId}
        authToken={authToken || ''}
        onClose={() => setRedeemHolding(null)}
        onPlacedSuccess={async () => {
          dispatch(
            showBanner({
              text: 'Redemption request placed. Proceeds will credit your wallet after execution.',
              variant: 'success-banner',
            }),
          )
          await reloadWithdrawals()
        }}
      />
      {investmentToCancel?.id && investmentToCancel.opportunity_id ? (
        <CancelInvestmentModal
          isOpen={true}
          onClose={() => setInvestmentToCancel(null)}
          investmentId={investmentToCancel.id}
          opportunityId={investmentToCancel.opportunity_id}
          projectName={investmentToCancel.opportunity_title || 'Investment'}
          investmentAmount={String(investmentToCancel.amount)}
          currencyCode='USD'
          refundsToWallet={investmentToCancel.status !== 'INITIATED'}
          refundAmount={
            investmentToCancel.shares && Number(investmentToCancel.final_amount) > 0
              ? String(investmentToCancel.final_amount)
              : undefined
          }
          onCancelSuccess={async () => {
            setInvestmentToCancel(null)
            dispatch(
              showBanner({
                text: 'Investment cancelled',
                variant: 'success-banner',
              }),
            )
            await reloadOngoingInvestments()
          }}
        />
      ) : null}
      <OngoingInvestmentDetailsModal
        isOpen={investmentToView !== null}
        investment={investmentToView}
        onClose={() => setInvestmentToView(null)}
        onCompletePayment={(inv) => {
          setInvestmentToView(null)
          const titleSegment = inv.opportunity_title
            ? createSlug(inv.opportunity_title).replace(/^-+|-+$/g, '') || 'opportunity'
            : 'opportunity'
          navigate(`/invest-v2/${titleSegment}/${inv.opportunity_id}`)
        }}
        onViewPaymentSummary={(inv) => {
          const titleSegment = inv.opportunity_title
            ? createSlug(inv.opportunity_title).replace(/^-+|-+$/g, '') || 'opportunity'
            : 'opportunity'
          navigate(`/payment/${titleSegment}/${inv.opportunity_id}/payment-summary`)
        }}
      />
      <TransactionDetailsModal
        isOpen={transactionToView !== null}
        transaction={transactionToView}
        onClose={() => setTransactionToView(null)}
      />
      <OngoingWithdrawalDetailsModal
        isOpen={withdrawalToView !== null}
        withdrawal={withdrawalToView}
        authToken={authToken || ''}
        clientId={resolvedClientId}
        onClose={() => setWithdrawalToView(null)}
      />
      {withdrawalToCancel ? (
        <CancelWithdrawalModal
          isOpen={true}
          onClose={() => setWithdrawalToCancel(null)}
          withdrawalId={withdrawalToCancel.id}
          projectName={withdrawalToCancel.opportunity_title || 'Redemption'}
          withdrawalAmount={String(withdrawalToCancel.amount)}
          completedSteps={withdrawalToCancel.metadata?.completedSteps ?? 0}
          onCancelSuccess={async () => {
            setWithdrawalToCancel(null)
            dispatch(
              showBanner({
                text: 'Redemption cancelled',
                variant: 'success-banner',
              }),
            )
            await reloadWithdrawals()
          }}
        />
      ) : null}
    </Fragment>
  )
}

export { DashboardV2Container }
